import { ArrowLeftIcon } from "lucide-react";
import backgroundImage from "../../assets/aitu-music.png";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { usePreserveQueryParamsNavigate } from "@/hooks/usePreserveQueryParamsNavigate";
import { useLocation } from "react-router";
import { SubscriptionDetailedViewProps } from "@/types/navigation";
import { useState } from "react";
import { cn, getCurrencySymbol, getProductPrice } from "@/lib/utils";
import i18n from "@/i18n";
import StatusIndicator from "@/components/customComponents/statusIndicator";
import ActionBlock from "@/components/customComponents/actionBlock";

const DetailedSubscriptionScreen = () => {
  const { t } = useTranslation("subscription");
  const navigate = usePreserveQueryParamsNavigate();
  const handleBack = () => {
    navigate("/");
  };

  const { state }: { state: SubscriptionDetailedViewProps } = useLocation();

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const toggleDescription = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const getStatus = (): "active" | "suspended" | "paymentError" => {
    if (state.paymentError) {
      return "paymentError";
    }
    if (state.suspended) {
      return "suspended";
    }
    return "active";
  };

  const getSubscriptionStatusText = () => {
    if (state.paymentError) {
      return (
        <div>
          <span className="text-system-gray-light">{t("paymentFailure")}</span>
        </div>
      );
    }
    if (state.suspended) {
      return (
        <div>
          <span className="text-system-gray-light">{t("paymentProcessing")}</span>
        </div>
      );
    }

    if (state.dateTill && !state.cancelDate) {
      const isDueToday =
        new Date(state.dateTill).toDateString() === new Date().toDateString();
      return (
        <span className="text-system-gray-light">
          {t("nextPayment", {
            date: isDueToday
              ? new Date(state.dateTill).toLocaleTimeString(i18n.language, {
                timeStyle: "short",
              })
              : new Date(state.dateTill).toLocaleDateString(i18n.language, {
                dateStyle: "short",
              }),
          })}
        </span>
      );
    }

    if (state.cancelDate && state.dateTill) {
      const isDueToday =
        new Date(state.dateTill).toDateString() === new Date().toDateString();
      return (
        <span className="text-system-gray-light">
          {t("subscriptionActiveUntil", {
            date: isDueToday
              ? new Date(state.dateTill).toLocaleTimeString(i18n.language, {
                timeStyle: "short",
              })
              : new Date(state.dateTill).toLocaleDateString(i18n.language, {
                dateStyle: "short",
              }),
          })}
        </span>
      );
    }
  };

  const getSubscriptionTypeText = () => {
    if (state.subscriptionType === "personal") {
      return t("personal");
    }
    return t("group");
  };

  return (
    <div className="relative h-screen">
      <div
        id="header"
        className="w-full \h-14 flex justify-between items-center py-4 text-primary-brand-light absolute top-6"
      >
        <Button
          onClick={handleBack}
          variant="link"
          className="text-2xl [&_svg]:h-6 [&_svg]:w-6"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </Button>
      </div>
      <div className="overflow-scroll">
        <img alt="aitu music banner" className="w-full" src={backgroundImage} />
        <div
          id="content"
          className="flex gap-2 flex-col px-4 pt-2 tracking-[0.2px]"
        >
          <div className="bg-system-bars-light rounded-lg p-2 ">
            <div className="gap-3 flex flex-col">
              <span className="font-medium text-wrap">{state.title}</span>
              <span
                onClick={toggleDescription}
                className={cn(
                  "font-normal text-sm text-system-gray-light text-ellipsis overflow-hidden max-h-[190px] cursor-pointer",
                  {
                    "line-clamp-2": !isDescriptionExpanded,
                    "line-clamp-none": isDescriptionExpanded,
                  },
                )}
              >
                {state.description}
              </span>
            </div>
          </div>
          <div className="bg-system-bars-light rounded-lg p-2">
            <div className="flex gap-1">
              <div className="gap-2 flex flex-col">
                <span>
                  {getSubscriptionTypeText()},{" "}
                  {getProductPrice(state.price ?? BigInt(0), i18n.language)}{" "}
                  {getCurrencySymbol(state.currencyCode ?? 0)}
                </span>
                <div className="flex gap-1 items-center">
                  <StatusIndicator status={getStatus()} />
                  <span className="text-system-gray-light">
                    {getSubscriptionStatusText()}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <ActionBlock
            variant={state.variant}
            subscriptionId={state.subscriptionId}
            isDisabled={!!state.cancelDate}
          />
        </div>
      </div>
    </div>
  );
};

export default DetailedSubscriptionScreen;
